// Firebase 인증 컨텍스트
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // 회원가입
  async function signup(email, password, profileData) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 사용자 프로필 생성
      const userData = {
        uid: user.uid,
        email: user.email,
        name: profileData.name,
        phone: profileData.phone,
        role: 'candidate', // 기본 역할
        status: 'active',
        userType: profileData.userType || '',
        preferredJobs: profileData.preferredJobs || [],
        availableDays: profileData.availableDays || [],
        availableTimeSlots: profileData.availableTimeSlots || [],
        notificationConsent: profileData.notificationConsent || false, // 알림 수신 동의 여부
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // 구인풀 병합 정보가 있는 경우 추가
      if (profileData.mergedFromCandidatePool) {
        userData.mergedFromCandidatePool = true;
        userData.notes = profileData.notes || '';
      }

      // Firestore에 사용자 정보 저장
      await setDoc(doc(db, 'users', user.uid), userData);

      // 구인풀에서 병합된 경우 기존 candidate 레코드 삭제
      if (profileData.candidatePoolId) {
        try {
          await deleteDoc(doc(db, 'users', profileData.candidatePoolId));
          console.log('기존 구인풀 레코드 삭제 완료:', profileData.candidatePoolId);
        } catch (deleteError) {
          console.error('구인풀 레코드 삭제 오류:', deleteError);
          // 삭제 실패해도 회원가입은 계속 진행
        }
      }

      // 디스플레이 이름 업데이트
      await updateProfile(user, {
        displayName: profileData.name
      });

      return user;
    } catch (error) {
      console.error('회원가입 오류:', error);
      throw error;
    }
  }

  // 로그인
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // 로그아웃
  function logout() {
    return signOut(auth);
  }

  // 비밀번호 재설정
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // 사용자 프로필 불러오기
  async function fetchUserProfile(uid) {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data();
      }
      return null;
    } catch (error) {
      console.error('프로필 불러오기 오류:', error);
      return null;
    }
  }

  // 인증 상태 변경 감지
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        const profile = await fetchUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

